import { performanceDiagnostics } from '@/diagnostics/browserDiagnostics';
import { authorities } from '@/simulation/native/authority';
import { ownVisualAssignment, ownVisualSignals } from '@/utils/OwnedBundleVisualData';
import { runtimeGraphIds } from './logic/RuntimeGraphIds';
import type { NodeHandle } from '@/models/logic/RuntimeHandles';
import { TileType } from '@/models/TileTypes';
import { makeAutoObservable, reaction } from 'mobx';
import { ChipInstanceId } from '@/types/ids';
import type {
  ICircuitModel,
  IChipInstanceModel,
  ILogicCircuitModel,
} from '@/interfaces/models';
import { LogicGraph } from './logic/LogicGraph';
import type { LogicNodeInstance } from './logic/LogicNodeInstance';
import { GraphManager } from './logic/GraphManager';
import { graphEditPatch } from './logic/GraphEditPatch';
import type { GraphEditPatch } from './logic/GraphTopology';
import { LogicSimulation } from './logic/LogicSimulation';
import { Signal } from './data/Signal';
import { allSides, CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import { tileDefinitionRegistry } from './tiles/TileRegistry';
import { TileInstance } from './tiles/TileInstance';
import Context from '@/context';
import type { ChipId } from '@/types/ids';
import { isRecursiveBoundary } from '@/utils/recursiveChipBoundary';
import {
  checkpointTopology,
  validateSimulationCheckpoint,
  type SimulationCheckpoint,
} from './logic/SimulationCheckpoint';

export interface VisualPublicationScheduler {
  request(callback: () => void): number;
  cancel(handle: number): void;
  isVisible?(): boolean;
  onHidden?(callback: () => void): () => void;
  isObserved?(): boolean;
  onObserved?(callback: () => void): () => void;
}

interface TilePublicationBinding {
  generation: number;
  outputOnly: boolean;
  nodeIds: TileInstance['logicNodeIds'];
  ioSides: TileInstance['effectiveIoSides'];
  sides: Array<{
    side: Side;
    nodeId: NodeHandle;
    node: LogicNodeInstance;
    io: TileSideIOType;
  }>;
}

export class LogicCircuitModel implements ILogicCircuitModel {
  #nativeSnapshotModel = undefined;
  /** Brand check rejects opaque proxies before inspecting any snapshot hooks. */
  static isNativeSnapshotModel(value: unknown): value is LogicCircuitModel {
    return (
      !!value &&
      typeof value === 'object' &&
      #nativeSnapshotModel in value &&
      Object.getPrototypeOf(value) === LogicCircuitModel.prototype
    );
  }

  recursionOwnerId?: ChipId;
  runtimeRevision = 0;
  logicGraph: LogicGraph;
  graphManager: GraphManager;
  logicSimulation: LogicSimulation;

  chipInstances = new Map<ChipInstanceId, IChipInstanceModel>();
  circuitModel: ICircuitModel;
  onGraphReady?: (graph: LogicGraph) => void;
  onAfterRebuild?: () => void;
  private context: Context;
  private disposers: Array<() => void> = [];
  private rebuildTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingRebuild: (() => void) | null = null;
  private disposed = false;
  private cooperativeBuildDepth = 0;
  nodeIdToTile = new Map<NodeHandle, TileInstance[]>();
  // Tiles that have syncStateToNodes (e.g. Lever, Button) - precomputed for fast pre-update
  private tilesWithPreSync: TileInstance[] = [];
  private preSyncTilesByNode = new Map<NodeHandle, TileInstance[]>();

  private publishedOutputs = new Map<
    NodeHandle,
    { node: LogicNodeInstance; output: boolean }
  >();
  private publicationGraph?: LogicGraph;
  private publicationGeneration = 0;
  private publicationBindings = new WeakMap<TileInstance, TilePublicationBinding>();

  private visualFrame?: number;
  private pendingVisualNodeIds = new Set<NodeHandle>();
  private pendingVisualGraph?: LogicGraph;
  private pendingRuntimeChange = false;
  private canDeferVisuals = false;

  constructor(
    circuitModel: ICircuitModel,
    simulationConfig: {
      maxSyncIterations: number;
      asyncDelay: number;
      maximumSpeed?: boolean;
    },
    context: Context,
    private readonly visualScheduler?: VisualPublicationScheduler,
    private readonly compileOnly = false,
  ) {
    this.circuitModel = circuitModel;
    this.context = context;
    this.graphManager = new GraphManager(
      circuitModel.tileManagerModel,
      circuitModel.connectionModel,
      undefined,
      runtimeGraphIds(context),
    );
    this.logicGraph = this.graphManager.graph;
    this.logicSimulation = new LogicSimulation(this.logicGraph);
    if (context.startPaused) this.logicSimulation.stop();

    makeAutoObservable(
      this,
      {
        nodeIdToTile: false,
        publishedOutputs: false,
        publicationGraph: false,
        publicationGeneration: false,
        publicationBindings: false,
        visualScheduler: false,
        visualFrame: false,
        pendingVisualNodeIds: false,
        pendingVisualGraph: false,
        pendingRuntimeChange: false,
        canDeferVisuals: false,
        tilesWithPreSync: false,
        preSyncTilesByNode: false,
        context: false,
        onGraphReady: false,
        onAfterRebuild: false,
        rebuildGraphSteps: false,
        rebuildGraphForContinuationSteps: false,
        adoptContinuationGraph: false,
        disposers: false,
        rebuildTimer: false,
        pendingRebuild: false,
        disposed: false,
        cooperativeBuildDepth: false,
        compileOnly: false,
      } as any,
      {
        autoBind: true,
      },
    );

    this.disposers.push(
      reaction(
        () => context.ui?.debugModeEnabled,
        (enabled) => this.logicSimulation.debugTimers.setEnabled(enabled === true),
        { fireImmediately: true },
      ),
    );
    this.logicSimulation.onPreUpdate = this.syncTilesToState;
    this.logicSimulation.onPostUpdate = this.publishVisuals;
    this.logicSimulation.onFlushVisuals = this.flushPendingVisuals;
    const stopHidden = visualScheduler?.onHidden?.(this.flushPendingVisuals);
    if (stopHidden) this.disposers.push(stopHidden);
    const stopObserved = visualScheduler?.onObserved?.(this.flushPendingVisuals);
    if (stopObserved) this.disposers.push(stopObserved);

    // Rebuild graph when tiles change
    this.disposers.push(
      reaction(
        () => ({
          bundleChipIdentity: context.userPreferences?.bundleChipIdentityEnabled,
          tileVersion: this.circuitModel.tileManagerModel.structureVersion,
          connectionVersion: this.circuitModel.connectionModel?.topologyVersion,
          tiles: this.circuitModel.tileManagerModel.tiles.map((t) => {
            const type = t.tileType;
            const meta = t.meta;
            const location = t.location;
            const tileConnectionVersion =
              this.circuitModel.connectionModel?.connectionVersionForTile?.(t.id) ?? 0;

            return {
              id: t.id,
              x: location.x,
              y: location.y,
              type,
              rotation: meta.rotation,
              partOfChip: meta.partOfChip,
              cable: meta.cable,
              bundle: JSON.stringify(meta.bundle),
              bundleBit:
                type === TileType.BundleInterface ? JSON.stringify(t.state) : undefined,
              timerLabel:
                type === TileType.DebugTimerStart || type === TileType.DebugTimerEnd
                  ? (t.state as { label: string }).label
                  : undefined,
              connectionVersion: tileConnectionVersion,
              extensionConfiguration: type.startsWith('extension:')
                ? JSON.stringify(
                    tileDefinitionRegistry.get(type).graphConfiguration?.(t.state) ??
                      null,
                  )
                : undefined,
              duration:
                type === TileType.Clock
                  ? (t.state as { intervalMs: number }).intervalMs
                  : type === TileType.Delay
                    ? (t.state as { delayTimeMs: number }).delayTimeMs
                    : undefined,
            };
          }),
          chipInstances: (this.circuitModel.chips ?? [])
            .filter(
              (chip) =>
                !this.recursionOwnerId ||
                !isRecursiveBoundary(this.recursionOwnerId, chip),
            )
            .map((chip) => ({
              id: chip.id,
              chipId: chip.chipId,
              bundleWidth: chip.bundleWidth,
              rotation: chip.rotation,
              x: chip.originPoint.x,
              y: chip.originPoint.y,
              templateVersion: chip.chip?.templateGraphVersion ?? 0,
            })),
        }),
        (next, previous) => {
          if (context.isHydrating || this.cooperativeBuildDepth !== 0) return;
          const tileChanges =
            previous?.tileVersion === undefined
              ? undefined
              : this.circuitModel.tileManagerModel.tileChangesSince?.(
                  previous.tileVersion,
                );
          const connectionChanges = !this.circuitModel.connectionModel
            ? new Set<string>()
            : previous?.connectionVersion === undefined
              ? undefined
              : this.circuitModel.connectionModel.connectionChangesSince?.(
                  previous.connectionVersion,
                );
          const patch =
            previous &&
            tileChanges &&
            connectionChanges &&
            next.bundleChipIdentity === previous.bundleChipIdentity
              ? graphEditPatch(previous, next, tileChanges, connectionChanges)
              : undefined;
          this.rebuildGraph(patch);
        },
        {
          scheduler: (run) => {
            // Hydration explicitly builds in dependency order. Flush tracking now,
            // without timers racing the saved-state restoration between yield points.
            if (context.isHydrating) {
              run();
              return;
            }
            this.pendingRebuild = run;
            // Coalesce the current transaction without adding a fixed pause to
            // each completed edit. Rebuilding stays off the input handler stack.
            this.rebuildTimer = setTimeout(() => this.flushPendingRebuild(), 0);
          },
        },
      ),
    );

    // Sync simulation config
    this.disposers.push(
      reaction(
        () => ({ ...simulationConfig }),
        (config) => {
          if (config) {
            this.logicSimulation.setConfig(config);
          }
        },
        { fireImmediately: true },
      ),
    );

    const unsubscribe = context.eventBus.subscribe(
      (nodeId: NodeHandle, _reason?: string) => {
        if (this.disposed || !this.logicGraph.nodes.has(nodeId)) return;
        const owner = authorities.get(this);
        if (owner) {
          owner.input(nodeId);
          return;
        }
        this.logicSimulation.triggerUpdate(nodeId);
      },
    );
    if (unsubscribe) this.disposers.push(unsubscribe);
  }

  dispose() {
    if (this.disposed) return;
    this.disposed = true;
    this.cancelPendingVisuals();
    for (const dispose of this.disposers) dispose();
    this.disposers = [];
    if (this.rebuildTimer !== null) clearTimeout(this.rebuildTimer);
    this.rebuildTimer = null;
    this.pendingRebuild = null;
    this.logicSimulation.stop();
    this.logicSimulation.onPreUpdate = undefined;
    this.logicSimulation.onPostUpdate = undefined;
    this.logicSimulation.onFlushVisuals = undefined;
    this.onGraphReady = undefined;
    this.onAfterRebuild = undefined;
    this.nodeIdToTile.clear();
    this.tilesWithPreSync = [];
    this.preSyncTilesByNode.clear();
  }

  /** Flush the scheduled reaction before deterministic tool-driven simulation. */
  flushPendingRebuild() {
    if (this.rebuildTimer !== null) clearTimeout(this.rebuildTimer);
    this.rebuildTimer = null;
    const pending = this.pendingRebuild;
    this.pendingRebuild = null;
    if (!this.disposed) pending?.();
  }

  rebuildGraph(patch?: GraphEditPatch) {
    if (this.disposed) return;
    const owner = authorities.get(this);
    if (owner) {
      owner.fail(Error('Topology changed outside ordered edit'));
      return;
    }
    const timing = performanceDiagnostics.start('graph-rebuild');
    try {
      // Update chip instances for the GraphManager before rebuilding
      const chipInstances = this.circuitModel.chips ?? [];
      this.chipInstances = new Map(chipInstances.map((chip) => [chip.id, chip]));
      this.graphManager.chipInstances = this.recursionOwnerId
        ? chipInstances.filter(
            (chip) => !isRecursiveBoundary(this.recursionOwnerId!, chip),
          )
        : chipInstances;
      this.graphManager.rebuildGraph(patch);
      this.finishGraphRebuild();
    } finally {
      performanceDiagnostics.finish('graph-rebuild', timing, this.logicGraph.nodes.size);
    }
  }

  *rebuildGraphSteps(): Generator<void> {
    const owner = authorities.get(this);
    if (owner) {
      owner.fail(Error('Cooperative rebuild outside ordered edit'));
      throw Error('Release worker before rebuild');
    }
    if (this.disposed) return;
    const timing = performanceDiagnostics.start('graph-cooperative');
    this.cooperativeBuildDepth++;
    try {
      const chipInstances = this.circuitModel.chips ?? [];
      this.chipInstances = new Map(chipInstances.map((chip) => [chip.id, chip]));
      this.graphManager.chipInstances = this.recursionOwnerId
        ? chipInstances.filter(
            (chip) => !isRecursiveBoundary(this.recursionOwnerId!, chip),
          )
        : chipInstances;
      yield* this.graphManager.rebuildGraphSteps();
      // Keep topology capture and final state sync in separate browser tasks.
      yield;
      this.finishGraphRebuild(false);
    } finally {
      this.cooperativeBuildDepth--;
      performanceDiagnostics.finish(
        'graph-cooperative',
        timing,
        this.logicGraph.nodes.size,
      );
    }
  }

  /** Hydration-only topology/binding preparation. Never process the saved owner. */
  *rebuildGraphForContinuationSteps(): Generator<void> {
    if (!this.context.isHydrating || this.disposed)
      throw Error('Continuation bindings require unpublished hydration');
    const timing = performanceDiagnostics.start('graph-cooperative');
    this.cooperativeBuildDepth++;
    try {
      const instances = this.circuitModel.chips ?? [];
      this.chipInstances = new Map(instances.map((instance) => [instance.id, instance]));
      this.graphManager.chipInstances = this.recursionOwnerId
        ? instances.filter(
            (instance) => !isRecursiveBoundary(this.recursionOwnerId!, instance),
          )
        : instances;
      yield* this.graphManager.rebuildGraphSteps();
      this.finishGraphRebuild(false, true);
    } finally {
      this.cooperativeBuildDepth--;
      performanceDiagnostics.finish(
        'graph-cooperative',
        timing,
        this.logicGraph.nodes.size,
      );
    }
  }

  /** Compile proves authored semantics; adoption preserves saved native execution order/identity. */
  adoptContinuationGraph(checkpoint: SimulationCheckpoint): void {
    if (!this.context.isHydrating || this.disposed)
      throw Error('Continuation adoption requires unpublished hydration');
    const compiled = this.logicGraph;
    const compiledNodes = [...compiled.nodes.values()];
    const keys = (nodes: { id: string; runtimeKey?: string }[]) => {
      const byId = new Map<string, string>();
      const seen = new Set<string>();
      for (const node of nodes) {
        if (!node.runtimeKey || byId.has(node.id) || seen.has(node.runtimeKey))
          throw Error('Continuation requires unique native runtime keys');
        byId.set(node.id, node.runtimeKey);
        seen.add(node.runtimeKey);
      }
      return byId;
    };
    const savedKeys = keys(checkpoint.nodes);
    const compiledKeys = keys(
      compiledNodes.map((node) => ({ id: node.idText, runtimeKey: node.runtimeKey })),
    );
    const semantic = (topology: string, byId: Map<string, string>) => {
      const value = JSON.parse(topology);
      const key = (id: string) => {
        const result = byId.get(id);
        if (!result) throw Error('Unknown continuation topology binding');
        return result;
      };
      return JSON.stringify({
        nodes: value.nodes.map(
          ([id, type, runtimeKey, configuration]: [string, string, string, unknown]) => {
            if (key(id) !== runtimeKey) throw Error('Continuation topology key mismatch');
            return [runtimeKey, type, configuration];
          },
        ),
        edges: value.edges.map(([source, target]: [string, string]) => [
          key(source),
          key(target),
        ]),
      });
    };
    if (
      semantic(checkpoint.topology, savedKeys) !==
      semantic(checkpointTopology(compiled), compiledKeys)
    )
      throw Error('Continuation compiled topology/configuration/order mismatch');
    const raw = LogicGraph.fromDto(
      { nodes: checkpoint.nodes, edges: checkpoint.edges },
      runtimeGraphIds(this.context),
    );
    validateSimulationCheckpoint(checkpoint, raw);
    const rawByKey = new Map(
      [...raw.nodes.values()].map((node) => [node.runtimeKey!, node]),
    );
    const remap = new Map<NodeHandle, NodeHandle>();
    for (const node of compiledNodes) {
      const target = rawByKey.get(node.runtimeKey!)!;
      target.isBundleObserver = node.isBundleObserver;
      target.bundlePath = node.bundlePath && [...node.bundlePath];
      target.bundlePathLabels = node.bundlePathLabels && [...node.bundlePathLabels];
      remap.set(node.id, target.id);
    }
    const resolve = (id: NodeHandle) =>
      remap.get(this.graphManager.resolveNodeId(id)) ?? id;
    const retain = (id: NodeHandle) => {
      remap.set(id, resolve(id));
    };
    for (const tile of this.circuitModel.tiles) {
      for (const id of [
        ...Object.values(tile.logicNodeIds ?? {}),
        ...Object.values(tile.laneNodeIds ?? {}).flat(),
      ])
        if (id !== undefined) retain(id);
      // And and Bridge state IDs are also sides in the compiled tile binding.
    }
    const instances = [...this.chipInstances.values()].map((instance) => {
      const graph = new LogicGraph(runtimeGraphIds(this.context));
      for (const node of instance.logicGraph.nodes.values()) {
        retain(node.id);
        const id = remap.get(node.id)!;
        graph.nodes.set(id, raw.nodes.get(id) ?? node);
      }
      for (const edge of instance.logicGraph.edges.values())
        graph.addEdge(resolve(edge.sourceNodeId), resolve(edge.targetNodeId), edge.meta);
      for (const value of instance.templateToInstanceIdMap.values()) retain(value);
      for (const lane of instance.templateToInstanceIdMap.lanes.values())
        for (const value of lane.values()) retain(value);
      return { instance, graph };
    });
    // All validation completed before replacing any authored binding.
    for (const tile of this.circuitModel.tiles)
      tile.remapContinuationBindings(remap, raw);
    for (const { instance, graph } of instances) {
      instance.logicGraph = graph;
      for (const [key, value] of instance.templateToInstanceIdMap)
        instance.templateToInstanceIdMap.set(key, remap.get(value) ?? value);
      for (const lane of instance.templateToInstanceIdMap.lanes.values())
        for (const [key, value] of lane) lane.set(key, remap.get(value) ?? value);
    }
    // Discard compile-only incremental aliases/caches. The first subsequent edit
    // performs the ordinary complete rebuild from these rebound native inputs.
    this.graphManager = new GraphManager(
      this.circuitModel.tileManagerModel,
      this.circuitModel.connectionModel,
      [...this.chipInstances.values()],
      runtimeGraphIds(this.context),
    );
    this.graphManager.graph = raw;
    this.finishGraphRebuild(false, true);
  }

  private finishGraphRebuild(notifyTemplate = true, continuationOnly = false) {
    const indexDelta = this.graphManager.takeRebuildIndexDelta(this.logicGraph);
    const pendingNodes = indexDelta?.nativePatch
      ? new Set(this.pendingVisualNodeIds)
      : undefined;
    this.cancelPendingVisuals();
    const topologyChanged = this.logicGraph !== this.graphManager.graph;
    this.logicGraph = this.graphManager.graph;

    // Rebuild the nodeId -> tiles index BEFORE setGraph so that the
    // onPreUpdate callback (syncTilesToState) can access correct tile lists.
    // This ensures lever/button tile states override stale restored node state.
    if (topologyChanged) {
      if (indexDelta) {
        // Keep untouched node lists and control bindings. Only replaced graph
        // components can acquire different tile-to-node relationships.
        for (const nodeId of indexDelta.previousNodeIds) {
          for (const index of [this.nodeIdToTile, this.preSyncTilesByNode]) {
            const previous = index.get(nodeId);
            if (!previous) continue;
            const retained = previous.filter((tile) => !indexDelta.tileIds.has(tile.id));
            if (retained.length) index.set(nodeId, retained);
            else index.delete(nodeId);
          }
        }
        this.tilesWithPreSync = this.tilesWithPreSync.filter(
          (tile) => !indexDelta.tileIds.has(tile.id),
        );
      } else {
        this.nodeIdToTile.clear();
        this.tilesWithPreSync = [];
        this.preSyncTilesByNode.clear();
      }
      for (const tile of indexDelta?.tiles ?? this.circuitModel.tileManagerModel.tiles) {
        if (tile.logicNodeIds || tile.laneNodeIds) {
          for (const nodeId of new Set([
            ...Object.values(tile.logicNodeIds ?? {}),
            ...Object.values(tile.laneNodeIds ?? {}).flat(),
          ])) {
            const existing = this.nodeIdToTile.get(nodeId);
            if (existing) {
              existing.push(tile);
            } else {
              this.nodeIdToTile.set(nodeId, [tile]);
            }
          }
        }
        // Pre-compute which tiles need syncStateToNodes (e.g. Lever, Button)
        const def = tileDefinitionRegistry.get(tile.tileType);
        if (def.syncStateToNodes && (def.needsStateToNodeSync?.(tile) ?? true)) {
          this.tilesWithPreSync.push(tile);
          for (const nodeId of new Set(Object.values(tile.logicNodeIds ?? {}))) {
            const existing = this.preSyncTilesByNode.get(nodeId);
            if (existing) existing.push(tile);
            else this.preSyncTilesByNode.set(nodeId, [tile]);
          }
        }
      }
    }

    if (
      continuationOnly ||
      this.compileOnly ||
      (this.context.startPaused && this.context.isHydrating)
    ) {
      // The factory simulation still owns its empty graph. Assigning its new
      // graph directly avoids setGraph's consistency processing and scheduling.
      // Validation copies must inspect topology before any behavioral evaluation;
      // their runner owns initialization and bounded stepping afterward.
      this.logicSimulation.stop();
      this.logicSimulation.graph = this.logicGraph;
      this.onGraphReady?.(this.logicGraph);
      return;
    }

    // Sync tile state (lever toggles, button presses) to nodes BEFORE
    // setGraph's smart dirty marking, so that consistency checks account
    // for user interactions that happened since the last rebuild.
    this.syncTilesToState();

    const publish = this.logicSimulation.onPostUpdate;
    const patchNodes =
      pendingNodes &&
      this.canDeferVisuals &&
      publish === this.publishVisuals &&
      indexDelta!.tiles.every((tile) => {
        const definition = tileDefinitionRegistry.get(tile.tileType);
        return !definition.syncState || definition.syncStateIsVisualOnly === true;
      })
        ? pendingNodes
        : undefined;
    if (patchNodes)
      this.logicSimulation.onPostUpdate = (ids) => {
        for (const id of ids) patchNodes.add(id);
        publish?.call(this.logicSimulation, ids);
      };

    // Ensure the simulation fully converges before we sync tiles.
    // setGraph's tick may hit maxSyncIterations and schedule an async
    // continuation — that would render partially-converged state as
    // visible flicker. Drain all remaining dirty nodes synchronously.
    try {
      this.logicSimulation.setGraph(this.logicGraph);
      this.logicSimulation.drainUntilStable(false);
    } finally {
      if (patchNodes) this.logicSimulation.onPostUpdate = publish;
    }

    // Rebuilt bindings need publication even when restored outputs were unchanged.
    if (patchNodes) {
      // Publish changed bindings even if their restored output was unchanged.
      // Retained components keep their current visuals; pending/ticked outputs
      // above still participate, including pulses processed during rebuilding.
      for (const tile of indexDelta!.tiles) {
        this.publicationBindings.delete(tile);
        for (const id of Object.values(tile.logicNodeIds ?? {})) patchNodes.add(id);
        for (const ids of Object.values(tile.laneNodeIds ?? {}))
          for (const id of ids) patchNodes.add(id);
      }
      this.cancelPendingVisuals();
      if (patchNodes.size) this.syncStateToTiles(patchNodes);
    } else this.syncStateToTiles();
    this.onGraphReady?.(this.logicGraph);
    if (notifyTemplate) this.onAfterRebuild?.();
  }

  syncTilesToState(dirtyNodeIds?: ReadonlySet<NodeHandle>) {
    // Rebuilds sync all inputs once. Normal ticks visit only the dirty nodes' tiles.
    let tiles: Iterable<TileInstance> = this.tilesWithPreSync;
    if (dirtyNodeIds !== undefined) {
      let pending: Set<TileInstance> | undefined;
      const ids =
        dirtyNodeIds.size <= this.preSyncTilesByNode.size
          ? dirtyNodeIds
          : this.preSyncTilesByNode.keys();
      for (const id of ids) {
        if (!dirtyNodeIds.has(id)) continue;
        const matching = this.preSyncTilesByNode.get(id);
        if (!matching) continue;
        pending ??= new Set<TileInstance>();
        for (const tile of matching) pending.add(tile);
      }
      // Most propagation waves contain no controls with tile-owned input state.
      if (!pending) return;
      tiles = pending;
    }
    for (const tile of tiles) {
      if (!tile.logicNodeIds) continue;
      tileDefinitionRegistry
        .get(tile.tileType)
        .syncStateToNodes?.({ instance: tile, graph: this.logicGraph });
    }
  }

  private cancelPendingVisuals() {
    // A forced full publication/rebuild may supersede the frame, not its save invalidation.
    if (this.pendingRuntimeChange) this.runtimeRevision++;
    if (this.visualFrame !== undefined) this.visualScheduler?.cancel(this.visualFrame);
    this.visualFrame = undefined;
    this.pendingVisualNodeIds.clear();
    this.pendingVisualGraph = undefined;
    this.pendingRuntimeChange = false;
  }

  /** Force current visuals at pause, manual step, inspection, and snapshot boundaries. */
  flushPendingVisuals() {
    if (!this.pendingRuntimeChange && !this.pendingVisualNodeIds.size) {
      this.cancelPendingVisuals();
      return;
    }
    if (this.visualFrame !== undefined) this.visualScheduler?.cancel(this.visualFrame);
    this.visualFrame = undefined;
    const pending = this.pendingVisualNodeIds;
    const graph = this.pendingVisualGraph;
    const runtimeChanged = this.pendingRuntimeChange;
    this.pendingRuntimeChange = false;
    this.pendingVisualNodeIds = new Set();
    this.pendingVisualGraph = undefined;
    if (!this.disposed && graph === this.logicGraph) {
      // Snapshot tracking must wake even when pulses return to their prior
      // displayed value, or only internal persistent state changed this frame.
      if (runtimeChanged) this.runtimeRevision++;
      if (pending.size) this.syncStateToTiles(pending);
    }
  }

  private publishVisuals(processedNodeIds: ReadonlySet<NodeHandle>) {
    if (
      !this.visualScheduler ||
      !this.logicSimulation.isRunning ||
      this.visualScheduler.isVisible?.() === false ||
      !this.canDeferVisuals ||
      this.context.isHydrating
    ) {
      this.flushPendingVisuals();
      if (processedNodeIds.size) this.runtimeRevision++;
      this.syncStateToTiles(processedNodeIds);
      return;
    }
    if (this.pendingVisualGraph && this.pendingVisualGraph !== this.logicGraph)
      this.cancelPendingVisuals();
    if (!processedNodeIds.size) return;
    this.pendingRuntimeChange = true;
    const ids =
      processedNodeIds.size <= this.nodeIdToTile.size
        ? processedNodeIds
        : this.nodeIdToTile.keys();
    // Retain at most one ID per mapped node, never whole propagation histories.
    for (const id of ids)
      if (processedNodeIds.has(id) && this.nodeIdToTile.has(id))
        this.pendingVisualNodeIds.add(id);
    this.pendingVisualGraph = this.logicGraph;
    if (this.visualFrame === undefined)
      this.visualFrame = this.visualScheduler.request(() => {
        this.visualFrame = undefined;
        if (this.visualScheduler?.isObserved?.() === false) {
          // Keep simulation/save invalidation live without touching offscreen
          // tile metadata. The bounded node set flushes on reentry or capture.
          if (this.pendingRuntimeChange) {
            this.pendingRuntimeChange = false;
            this.runtimeRevision++;
          }
        } else this.flushPendingVisuals();
      });
  }

  syncStateToTiles(processedNodeIds?: ReadonlySet<NodeHandle>) {
    if (!processedNodeIds) {
      this.cancelPendingVisuals();
      // Unknown custom hooks may have synchronous side effects. Only explicitly
      // visual hooks are safe to sample, and extensions retain existing timing.
      this.canDeferVisuals = this.circuitModel.tileManagerModel.tiles.every((tile) => {
        const definition = tileDefinitionRegistry.get(tile.tileType);
        return (
          !tile.tileType.startsWith('extension:') &&
          (!definition.syncState || definition.syncStateIsVisualOnly === true)
        );
      });
    }
    // Only sync tiles whose logic nodes were processed, or all if no set provided.
    // Use a Set to deduplicate — a tile with multiple sides sharing a node ID
    // would otherwise be processed multiple times from nodeIdToTile lookups.
    const tilesToSync = new Set<TileInstance>();
    const partialPublication =
      processedNodeIds !== undefined && this.nodeIdToTile.size > 0;
    let usedCustomSynchronizer = false;

    if (!processedNodeIds || this.publicationGraph !== this.logicGraph) {
      this.publishedOutputs.clear();
      if (this.publicationGraph !== this.logicGraph) this.publicationGeneration++;
      this.publicationGraph = this.logicGraph;
    }
    if (processedNodeIds && this.nodeIdToTile.size > 0) {
      // Lifted instances can process many nodes with no visible tile in this
      // circuit. Intersect from the smaller collection before resolving outputs.
      const ids =
        processedNodeIds.size <= this.nodeIdToTile.size
          ? processedNodeIds
          : this.nodeIdToTile.keys();
      for (const nodeId of ids) {
        if (!processedNodeIds.has(nodeId)) continue;
        const tiles = this.nodeIdToTile.get(nodeId);
        if (!tiles) continue;
        const node = this.logicGraph.nodes.get(nodeId);
        const previous = this.publishedOutputs.get(nodeId);
        const unchanged =
          node !== undefined &&
          previous?.node === node &&
          previous.output === node.output;
        if (node) {
          if (previous?.node === node) previous.output = node.output;
          else this.publishedOutputs.set(nodeId, { node, output: node.output });
        }
        for (const tile of tiles) {
          // A replacement node or side map can keep the same output but still
          // needs fresh bindings and a publication to its new visual side.
          if (previous && previous.node !== node) this.publicationBindings.delete(tile);
          const binding = this.publicationBindings.get(tile);
          if (
            unchanged &&
            binding?.outputOnly &&
            binding.generation === this.publicationGeneration &&
            binding.nodeIds === tile.logicNodeIds &&
            binding.ioSides === tile.effectiveIoSides
          )
            continue;
          tilesToSync.add(tile);
        }
      }
    } else {
      for (const tile of this.circuitModel.tileManagerModel.tiles) {
        if (tile.logicNodeIds || tile.laneNodeIds) {
          tilesToSync.add(tile);
        }
      }
    }

    const graph = this.logicGraph;
    const nodes = graph.nodes;
    // Count-free cable sides share node arrays. Resolve each payload once per
    // publication, and share its immutable signal snapshot across those sides.
    const laneSnapshots = new Map<
      NodeHandle[],
      {
        signals: Signal[];
        observableSignals?: Signal[];
        comparisons: Map<Signal[] | undefined, boolean>;
      }
    >();
    const emptyLanes: NodeHandle[] = [];
    for (const tile of tilesToSync) {
      if (tile.laneNodeIds) {
        let changed = false;
        tile.laneInputs ??= ownVisualAssignment(tile, 'laneInputs', {});
        tile.laneOutputs ??= ownVisualAssignment(tile, 'laneOutputs', {});
        for (const side of allSides) {
          const laneNodes = tile.laneNodeIds[side] ?? emptyLanes;
          let snapshot = laneSnapshots.get(laneNodes);
          if (!snapshot) {
            snapshot = {
              signals: laneNodes.map((id) =>
                nodes.get(id)?.output ? Signal.HIGH : Signal.LOW,
              ),
              comparisons: new Map(),
            };
            laneSnapshots.set(laneNodes, snapshot);
          }
          const previous = tile.laneOutputs[side];
          let differs = snapshot.comparisons.get(previous);
          if (differs === undefined) {
            // Custom synchronizers may mutate a payload already published to
            // an earlier tile in this pass. Preserve that shared live view.
            const signals = snapshot.observableSignals ?? snapshot.signals;
            differs =
              signals.length !== previous?.length ||
              signals.some((signal, lane) => previous?.[lane] !== signal);
            snapshot.comparisons.set(previous, differs);
          }
          if (differs) {
            // Most publications leave bundle payloads unchanged. Build MobX
            // arrays only when a side actually needs a new immutable snapshot.
            snapshot.observableSignals ??= ownVisualSignals(snapshot.signals);
            tile.laneInputs[side] = snapshot.observableSignals;
            tile.laneOutputs[side] = snapshot.observableSignals;
            changed = true;
          }
        }
        if (changed) tile.renderVersion++;
        if (!tile.logicNodeIds) continue;
      }
      const definition = tileDefinitionRegistry.get(tile.tileType);
      if (definition.syncState) {
        this.publicationBindings.delete(tile);
        // A custom synchronizer may also affect another tile's metadata.
        usedCustomSynchronizer = true;
        // A custom synchronizer owns the whole tile, not one call per side.
        definition.syncState({ instance: tile, graph });
        continue;
      }
      const meta = tile.meta;
      const inputs = meta.inputs;
      const outputs = meta.outputs;
      // Side topology is stable between rebuild/full publications. Keep live
      // node references, but read the current metadata containers above: import
      // or edit commands may replace metadata without changing connectivity.
      const nodeIds = tile.logicNodeIds!;
      const effectiveIo = tile.effectiveIoSides;
      const cacheable = !tile.tileType.startsWith('extension:');
      let binding = cacheable ? this.publicationBindings.get(tile) : undefined;
      if (
        !processedNodeIds ||
        !binding ||
        binding.generation !== this.publicationGeneration ||
        binding.nodeIds !== nodeIds ||
        binding.ioSides !== effectiveIo
      ) {
        // Full publications still reread every public binding and IO field.
        // Reuse the ordinary side records when those reads agree, instead of
        // allocating four new records per unchanged wire after a local edit.
        let nextSides: TilePublicationBinding['sides'] | undefined;
        let count = 0;
        let outputOnly = true;
        for (const side of allSides) {
          const nodeId = nodeIds[side];
          if (nodeId === undefined) continue;
          const io = effectiveIo?.[side] ?? definition.ioSides[side];
          if (!io || io.type === TileSideIOType.NONE) continue;
          const node = nodes.get(nodeId);
          if (io.type === TileSideIOType.INPUT) outputOnly = false;
          if (node) {
            const type = io.type;
            const previous = binding?.sides[count];
            const same =
              previous?.side === side &&
              previous.nodeId === nodeId &&
              previous.node === node &&
              previous.io === type;
            if (!same && !nextSides) nextSides = binding?.sides.slice(0, count) ?? [];
            if (nextSides)
              nextSides.push(same ? previous! : { side, nodeId, node, io: type });
            count++;
          }
        }
        const sides =
          nextSides ??
          (binding?.sides.length === count
            ? binding.sides
            : (binding?.sides.slice(0, count) ?? []));
        if (binding) {
          binding.nodeIds = nodeIds;
          binding.ioSides = effectiveIo;
          binding.sides = sides;
          binding.outputOnly = outputOnly;
          binding.generation = this.publicationGeneration;
        } else
          binding = {
            generation: this.publicationGeneration,
            nodeIds,
            ioSides: effectiveIo,
            sides,
            outputOnly,
          };
        if (cacheable) this.publicationBindings.set(tile, binding);
      }
      for (const { side, nodeId, node, io } of binding.sides) {
        if (io === TileSideIOType.INPUT) {
          const input = graph.getNodeInput(nodeId) ? Signal.HIGH : Signal.LOW;
          if (inputs[side] !== input) inputs[side] = input;
        } else {
          const output = node.output ? Signal.HIGH : Signal.LOW;
          if (io === TileSideIOType.INPUT_OUTPUT && inputs[side] !== output) {
            inputs[side] = output;
          }
          if (outputs[side] !== output) outputs[side] = output;
        }
      }
    }

    // Mirror logic tile outputs to paired structure tiles so the
    // structure editor shows live signal state.
    this.syncOutputsToStructureTiles(
      partialPublication && !usedCustomSynchronizer ? tilesToSync : undefined,
    );
  }

  private syncOutputsToStructureTiles(selectedTiles?: ReadonlySet<TileInstance>) {
    if (selectedTiles?.size === 0) return;
    const chipEditor = this.context?.editor?.currentChipEditor;
    if (!chipEditor) return;

    const chipModel = chipEditor.chipModel;
    for (const link of chipModel.interfaceLinks) {
      const logicTile = link.tiles[CircuitType.ChipLogic];
      if (!logicTile || (selectedTiles && !selectedTiles.has(logicTile))) continue;
      const structureTile = link.tiles[CircuitType.ChipStructure];
      if (!structureTile) continue;

      for (const side of Object.values(Side)) {
        const li = logicTile.meta.inputs[side];
        const lo = logicTile.meta.outputs[side];
        if (structureTile.meta.inputs[side] !== li) structureTile.meta.inputs[side] = li;
        if (structureTile.meta.outputs[side] !== lo)
          structureTile.meta.outputs[side] = lo;
      }
    }
  }
}
